import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ExternalLink } from "lucide-react";
import { useLang } from "../i18n/LangProvider";

const Contact = () => {
  const { t } = useLang();
  const mapsUrl =
    "https://www.google.com/maps/search/?api=1&query=Chem.+Edouard-Sarasin+26,+1218+Le+Grand-Saconnex";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-obsidian py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="divider-line" />
              <span className="overline">{t.contact.overline}</span>
            </div>
            <h2 className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-10">
              {t.contact.title}
            </h2>

            <div className="space-y-8">
              <div className="flex gap-5">
                <MapPin size={20} className="text-copper mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-2">{t.contact.overline}</p>
                  <p className="text-ivory text-lg font-serif">{t.contact.address}</p>
                  <p className="text-ivory/65 text-sm">{t.contact.city}</p>
                </div>
              </div>

              <div className="flex gap-5">
                <Phone size={20} className="text-copper mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-2">{t.contact.phoneLabel}</p>
                  <a
                    data-testid="contact-phone-link"
                    href={`tel:0227981011`}
                    className="text-ivory text-lg font-serif link-underline"
                  >
                    {t.contact.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-5">
                <Clock size={20} className="text-copper mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-2">{t.contact.hoursLabel}</p>
                  <p className="text-ivory text-base font-serif">{t.contact.hoursWeek}</p>
                  <p className="text-ivory/55 text-sm mt-1">{t.contact.hoursClosed}</p>
                </div>
              </div>
            </div>

            <a
              data-testid="contact-directions-link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary mt-10"
            >
              {t.contact.directions} <ExternalLink size={14} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative h-[480px] md:h-[560px] border border-edge overflow-hidden"
          >
            <iframe
              title="Grizzly location map"
              src="https://www.google.com/maps?q=Chem.+Edouard-Sarasin+26,+1218+Le+Grand-Saconnex&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.7) contrast(0.9) brightness(0.85) invert(0.92) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
            <div className="absolute top-0 left-0 w-12 h-12 border-t border-l border-copper pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-b border-r border-copper pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
